import { ReactNode, Component, CSSProperties, ReactElement } from 'react';
import _ from 'lodash';
import { SketchPicker, ChromePicker, BlockPicker } from 'react-color';
import * as WidgetsThingsStructs from "../WidgetsThingsStructs";
import { SettingsList } from '@Primitive/SettingsList';
import { SettingsType } from '@Primitive/SettingsListType';
import { IWidgetThingsClass } from "../Utils/IWidgetThingsClass";
import { getBool, getRandomSmallKey, isDefined, IsNullOrEmpty } from '@Utils/Micro';
import { IWidgetDescription, IWidgetSettingsProps, WidgetDescriptionToPlegmaThing } from '@UI/Widgets/WidgetStructs';
import { WebNode } from '@API/WebNode';
import { WebThing, WebThingPort } from '@API/WebThing';
import { Plegma } from '@API/Api';
import { PortData, WebThingPortType } from '@API/WebNodeTypes';
import { buildStructureFromPath } from '@Primitive/TreeStructureHelper';
import { FolderItemType, FolderStructure, TreeStructure } from '@Primitive/TreeStructure';
import { DebugEx } from '@Utils/DebugEx';
import { ColorPaletteMode } from '../UserInput/ColorPalette';
import { getStringFromRGBA } from '../Gauges/GaugesHelper';
import type { LocalizationMap } from '@Primitive/Localization/Localization';
import Toggle from '@Primitive/Toggle';
import { IconButton, Button } from '@Primitive/Button';
import Popover from '@Primitive/Popover';
import { Radio, RadioGroup } from 'rsuite';
// =======================================================================================================
export interface ColorPickerData {
    pickerType: ColorPaletteMode;
    color: string;
}
export interface IRadioButton {
    /**
     * Internal key for handling re-ordering
     */
    key?: string;
    label: string;
    value: string | number;
}
export interface IRadioButtons {
    /**
     * Used as port key in form widget.
     */
    key: string;
    buttons: IRadioButton[];
    horizontal?: boolean;
}
export interface ItemData {
    key: string;
    name: string;
    description?: string;
    path: string;
    order: number;
    type: FolderItemType;
    extraData?: ColorPickerData | IRadioButtons | {};
}
export interface CommonListData {
    [key: string]: ItemData;
}
interface ListTree {
    structure: FolderStructure[];
}
interface CommonListState {
    listTree: ListTree;
}
interface CommonListSettingsState { }
// =======================================================================================================
class CommonList extends Component<WidgetsThingsStructs.PartialIWidgetThingsProps<CommonListData>, CommonListState> {
    // ---------------------------------------------------------------------------------------------------
    constructor(props: WidgetsThingsStructs.PartialIWidgetThingsProps<CommonListData>) {
        super(props);

        // Initialize component's state.
        this.state = { listTree: { structure: [] } };
    }
    // ---------------------------------------------------------------------------------------------------
    componentDidMount() {
        this.updateTree();
    }
    // ---------------------------------------------------------------------------------------------------
    componentWillReceiveProps(nextProps: WidgetsThingsStructs.PartialIWidgetThingsProps<CommonListData>) {
        this.updateTree(nextProps);
    }
    // ---------------------------------------------------------------------------------------------------
    updateTree = (props: WidgetsThingsStructs.PartialIWidgetThingsProps<CommonListData> = this.props) => this.setState({
        listTree: this.createCommonListTree(
            props.WidgetData,
            props.PortsStates,
            props.LocalizationMap
        )
    })
    // ---------------------------------------------------------------------------------------------------
    private createCommonListTree(
        widgetData: CommonListData,
        portsStates: { [key: string]: PortData },
        localizationMap: LocalizationMap): ListTree {
        let listTree: ListTree = { structure: [] };

        // Check that there are items in widget's data.
        if (_.keys(widgetData).length > 0) {
            let parentPaths = { '/': 0 },
                sortedItems = _.sortBy(widgetData, x => x.order),
                structure = [],
                id = 1;

            _.forEach(sortedItems, item => {
                if (_.hasIn(parentPaths, item.path)) {
                    // Add item into tree's structure.
                    structure = this.addItemIntoTree(structure, item, id, parentPaths[item.path], portsStates, localizationMap);
                    id++;
                } else {
                    // Split path and create folder for every piece.
                    let splittedPaths: string[] = item.path.split('/');
                    splittedPaths.pop();
                    splittedPaths.splice(0, 1);

                    if (splittedPaths.length > 0) {
                        let path: string = '/',
                            parentId: number = 0;

                        for (var i = 0; i < splittedPaths.length; i++) {
                            if (_.hasIn(parentPaths, path + splittedPaths[i] + '/')) {
                                path = path + splittedPaths[i] + '/';
                                parentId = parentPaths[path];
                            } else {
                                // Add new folder into tree's structure.
                                structure.push({
                                    _id: id,
                                    children: [],
                                    path: path,
                                    type: FolderItemType.Folder,
                                    label: splittedPaths[i],
                                    primaryText: splittedPaths[i],
                                    parentId: parentId,
                                });

                                path = path + splittedPaths[i] + '/';
                                parentPaths[path] = id;
                                parentId = id;
                                id++;
                            }
                        }
                        structure = this.addItemIntoTree(structure, item, id, parentPaths[item.path], portsStates, localizationMap);
                        id++;
                    }
                }
            });
            listTree.structure = buildStructureFromPath(structure);
        }
        return listTree;
    }
    // ---------------------------------------------------------------------------------------------------
    private addItemIntoTree(structure: FolderStructure[], item: ItemData, id: number, parentId: number,
        portsStates: { [key: string]: PortData },
        localizationMap: LocalizationMap): FolderStructure[] {
        let rightIcon: ReactNode = null,
            leftIcon: ReactNode = null,
            style: CSSProperties = null,
            primaryText: string = localizationMap[item.key],
            secondaryText: string = localizationMap[getKeyForDescription(item.key)],
            nestedItem: Array<ReactElement<any>> = null;

        if (!isDefined(primaryText))
            primaryText = item.name;

        if (!isDefined(secondaryText))
            secondaryText = item.description;

        // Create item's properties based on its type.
        switch (item.type) {
            case FolderItemType.Switch: {
                rightIcon = this.rightToggleElement(portsStates, item.key);
                break;
            }

            case FolderItemType.Button: {
                rightIcon = this.rightIconButtonElement(portsStates, item.key, primaryText);
                style = { fontWeight: 'bold' };
                break;
            }

            case FolderItemType.ColorPicker: {
                let port: PortData = portsStates[item.key];

                leftIcon = this.leftIconForColorPicker(port, item);
                style = { fontWeight: 'bold' };
                rightIcon = isDefined(port) ?
                    <IconButton
                        iconClassName='fas fa-check'
                        iconStyle={{ color: 'black' }}
                        disabled={!port?.IsActive}
                        onClick={() => this.props.SendPortEvent(item.key, (item.extraData as ColorPickerData).color)}
                    />
                    : null;
                break;
            }

            case FolderItemType.RadioButtons: {
                let port: PortData = portsStates[item.key];
                nestedItem = [
                    <RadioGroup
                        key={item.key}
                        style={{ padding: '4px 0' }}
                        name={primaryText}
                        defaultValue={port?.State}
                        value={port?.State}
                        onChange={v => {
                            if (port?.IsActive) {
                                this.props.SendPortEvent(item.key, v);
                                this.updateTree();
                            }
                        }}>
                        {_.map((item.extraData as IRadioButtons).buttons,
                            (r, i) => {
                                let btnLabel = localizationMap[getKeyForRadioBtnLabel(item.key, r.key)]
                                if (!isDefined(btnLabel))
                                    btnLabel = r.label;

                                return <Radio
                                    key={"radio-" + i}
                                    value={r.value}
                                    checked={r.value == port?.State}
                                    disabled={!port.IsActive}>
                                    {btnLabel}
                                </Radio>;
                            })}
                    </RadioGroup>]
                break;
            }

            default:
                break;
        }

        // Add new item into tree's structure.
        structure.push({
            _id: id,
            children: [],
            path: item.path,
            type: item.type,
            key: item.key,
            parentId: parentId,
            primaryText: primaryText,
            secondaryText: secondaryText,
            rightIcon: rightIcon,
            style: style,
            leftIcon: leftIcon,
            nestedItem: nestedItem
        });

        return structure;
    }
    // ---------------------------------------------------------------------------------------------------
    rightToggleElement(portsStates: { [key: string]: PortData }, portKey: string): ReactElement {
        let port: PortData = portsStates[portKey];

        if (isDefined(port))
            return <Toggle
                key={'toggle-' + portKey}
                disabled={!port.IsActive}
                onToggle={(isInputChecked) => this.props.SendPortEvent(portKey, isInputChecked)}
                toggled={getBool(port.State)}
            />;
        return null;
    }
    // ---------------------------------------------------------------------------------------------------
    rightIconButtonElement(portsStates: { [key: string]: PortData }, portKey: string, name: string): ReactElement {
        let port: PortData = portsStates[portKey];

        if (isDefined(port)) {
            return <Button
                key={'raised-button-' + portKey}
                height={35}
                disabled={!port.IsActive}
                label={name}
                style={{ color: 'rgba(0, 0, 0, 0.87)' }}
                onClick={() => this.props.SendPortEvent(portKey, true)} />;
        }
        else
            return null;
    }
    // ---------------------------------------------------------------------------------------------------
    leftIconForColorPicker(port: PortData, item: ItemData): ReactElement {
        return (
            <Popover
                outsideNode={<div
                    style={{
                        padding: '5px',
                        background: '#fff',
                        borderRadius: '1px',
                        boxShadow: '0 0 0 1px rgba(0,0,0,.1)',
                        display: 'inline-block',
                        cursor: port?.IsActive ? 'pointer' : 'not-allowed',
                        opacity: port?.IsActive ? 1 : 0.2
                    }}
                    onClick={() => {
                        if (port?.IsActive)
                            this.setState((s: CommonListState) => {
                                s.listTree.structure = this.updateColorPicker(
                                    _.cloneDeep(this.state.listTree.structure), item
                                );
                                return s;
                            });
                    }}>
                    <div
                        style={{
                            width: '36px',
                            height: '14px',
                            borderRadius: '2px',
                            background: (item.extraData as ColorPickerData).color
                        }}
                    />
                </div>}
                boxNode={(() => {
                    switch ((item.extraData as ColorPickerData).pickerType) {
                        case ColorPaletteMode.Sketch:
                            return (
                                <SketchPicker
                                    color={(item.extraData as ColorPickerData).color}
                                    onChange={(color) => {
                                        (item.extraData as ColorPickerData).color = getStringFromRGBA(color.rgb);

                                        this.setState((prevState: CommonListState) => {
                                            prevState.listTree.structure =
                                                this.updateColorPicker(_.cloneDeep(this.state.listTree.structure), item);
                                            return prevState;
                                        });
                                    }}
                                />
                            );
                        case ColorPaletteMode.Chrome:
                            return (
                                <ChromePicker
                                    color={(item.extraData as ColorPickerData).color}
                                    onChange={(color) => {
                                        (item.extraData as ColorPickerData).color = getStringFromRGBA(color.rgb);

                                        this.setState((prevState: CommonListState) => {
                                            prevState.listTree.structure =
                                                this.updateColorPicker(_.cloneDeep(this.state.listTree.structure), item);
                                            return prevState;
                                        });
                                    }}
                                />
                            );
                        case ColorPaletteMode.Block:
                            return (
                                <BlockPicker
                                    color={(item.extraData as ColorPickerData).color}
                                    onChange={(color) => {
                                        (item.extraData as ColorPickerData).color = getStringFromRGBA(color.rgb);

                                        this.setState((prevState: CommonListState) => {
                                            prevState.listTree.structure =
                                                this.updateColorPicker(_.cloneDeep(this.state.listTree.structure), item);
                                            return prevState;
                                        });
                                    }}
                                />
                            );
                        default:
                            return null;
                    }
                })()} />
        );
    }
    // ---------------------------------------------------------------------------------------------------
    private updateColorPicker(structure: FolderStructure[], item: ItemData): FolderStructure[] {
        // Update color picker's left icon.
        _.forEach(structure, treeItem => {
            if (treeItem.type == FolderItemType.ColorPicker) {
                if (treeItem.key == item.key) {
                    treeItem.leftIcon = this.leftIconForColorPicker(this.props.PortsStates[item.key], item);
                }
            }
            else if (treeItem.children.length > 0) {
                treeItem.children = this.updateColorPicker(treeItem.children, item);
            }
        });
        return structure;
    }
    // ---------------------------------------------------------------------------------------------------
    render(): ReactElement {
        return (
            <div style={{ padding: WidgetsThingsStructs.defaultWidgetContainerPadding }}>
                <TreeStructure
                    FolderStructure={this.state.listTree.structure}
                    style={{ padding: 0 }} />
            </div>
        );
    }
}
// =======================================================================================================
class CommonListSettings extends Component<IWidgetSettingsProps<CommonListData>, CommonListSettingsState> {
    // ---------------------------------------------------------------------------------------------------
    constructor(props: IWidgetSettingsProps<CommonListData>) {
        super(props);

        // Initialize component's state.
        this.state = {
        };
    }
    // ---------------------------------------------------------------------------------------------------
    render(): ReactElement {
        return <SettingsList
            AutoSave={true}
            Data={{ items: _.sortBy(_.values(this.props.WidgetData), "order", "asc") }}
            onSaveData={(data) => {
                let values = {};
                _.forEach(data.items, (d, i) => {
                    d.order = i;
                    values[d.key] = d;
                });
                this.props.onWidgetDataUpdate(values);
            }}
            SettingsMap={
                [
                    {
                        name: "",
                        elements: [
                            {
                                type: SettingsType.ItemsMgmtDialog,
                                label: "Items",
                                dataPath: "items",
                                createNewItem: () => {
                                    let key = getRandomSmallKey();
                                    return {
                                        key: key,
                                        name: "",
                                        description: "",
                                        type: FolderItemType.Switch,
                                        path: "",
                                        order: 200,
                                        extraData: {
                                            pickerType: ColorPaletteMode.Sketch,
                                            buttons: [],
                                            color: "black",
                                            key: key
                                        }
                                    } as ItemData;
                                },
                                itemsInfo: [
                                    {
                                        type: SettingsType.String,
                                        dataPath: "name",
                                        label: "Name"
                                    },
                                    {
                                        type: SettingsType.String,
                                        dataPath: "description",
                                        label: "Description"
                                    },
                                    {
                                        type: SettingsType.String,
                                        dataPath: "path",
                                        label: "Path"
                                    },
                                    {
                                        type: SettingsType.DropDown,
                                        dataPath: "type",
                                        label: "Type",
                                        items: [
                                            { value: FolderItemType.Switch, label: "Switch" },
                                            { value: FolderItemType.Button, label: "Button" },
                                            { value: FolderItemType.ColorPicker, label: "ColorPicker" },
                                            { value: FolderItemType.RadioButtons, label: "RadioButtons" },
                                        ]
                                    },
                                    {
                                        type: SettingsType.DropDown,
                                        dataPath: "extraData.pickerType",
                                        label: "Color Type",
                                        items: [
                                            { value: ColorPaletteMode.Sketch, label: "Sketch" },
                                            { value: ColorPaletteMode.Chrome, label: "Chrome" },
                                            { value: ColorPaletteMode.Block, label: "Block" }
                                        ],
                                        isHiddenCell: (data, row) => row.type != FolderItemType.ColorPicker
                                    },
                                    {
                                        type: SettingsType.ColorPicker,
                                        dataPath: "extraData.color",
                                        label: "Color",
                                        isHiddenCell: (data, row) => row.type != FolderItemType.ColorPicker
                                    }
                                ]
                            }
                        ]
                    },
                    // ------------------------------------------------------------  Radio Buttons
                    {
                        name: "Radio Buttons",
                        elementsFunc: (data) => _.map(_.filter(data.items, (d) => d.type == FolderItemType.RadioButtons),
                            (d) => {
                                let index = _.findIndex(data.items, x => x.key == d.key);
                                return {
                                    type: SettingsType.ItemsMgmtDialog,
                                    label: d.name,
                                    dataPath: "items[" + index + "].extraData.buttons",
                                    dialogStaticSize: { w: 0.5, h: 0.8 },
                                    itemsInfo: [
                                        {
                                            label: "Label",
                                            type: SettingsType.String,
                                            dataPath: "label",
                                        },
                                        {
                                            label: "Value",
                                            type: SettingsType.String,
                                            dataPath: "value",
                                        }
                                    ],
                                    createNewItem: () => {
                                        let key = getRandomSmallKey();
                                        return {
                                            key: key,
                                            value: "val-" + key,
                                            label: "Option-" + key
                                        }
                                    }
                                };
                            })
                    },
                ]
            }
        />;
    }
}
// =======================================================================================================
export class CommonListWidget extends IWidgetThingsClass<CommonListData> {
    // ------------------------------------------------------------------------------------------------
    Render(key: string, width: number, height: number): ReactNode {
        return <CommonList />;
    }
    // ------------------------------------------------------------------------------------------------
    RenderWidgetSettings(key: string, width: number, height: number): ReactElement<any> {
        const parent = this.Parent;
        return <CommonListSettings
            key={'settings-' + key}
            width={width}
            height={height}
            WidgetData={_.cloneDeep(this.WidgetData)}
            WidgetDefaultValues={_.cloneDeep(this.Description.DefaultValues)}
            ThingKey={this.ThingKey}
            NodeKey={this.NodeKey}
            onWidgetDataUpdate={(d) => this.WidgetDataUpdate(d)}
            SettingListToolbar={isDefined(parent) ? parent.SettingsListToolbar : null}
        />;
    }
    // ------------------------------------------------------------------------------------------------
    private createThingPorts(widgetData: CommonListData): WebThingPort[] {
        let ports: WebThingPort[] = [];

        _.forEach(widgetData, item => {
            switch (item.type) {
                case FolderItemType.Switch:
                    ports.push(new WebThingPort(
                        {
                            Name: item.name,
                            PortKey: item.key,
                            Type: Plegma.ePortType.Boolean,
                            ioDirection: Plegma.ioPortDirection.InputOutput,
                            State: 'False'
                        },
                        WebThingPortType.Data));
                    break;

                case FolderItemType.Button:
                    ports.push(new WebThingPort(
                        {
                            Name: item.name,
                            PortKey: item.key,
                            Type: Plegma.ePortType.Boolean,
                            ioDirection: Plegma.ioPortDirection.Output,
                            State: 'False',
                        },
                        WebThingPortType.Trigger
                    ));
                    break;

                case FolderItemType.ColorPicker:
                    ports.push(new WebThingPort(
                        {
                            Name: item.name,
                            PortKey: item.key,
                            Type: Plegma.ePortType.String,
                            ioDirection: Plegma.ioPortDirection.Output,
                            State: 'rgba(0, 0, 0, 1)',
                        },
                        WebThingPortType.Data
                    ));
                    break;

                case FolderItemType.RadioButtons:
                    ports.push(new WebThingPort(
                        {
                            Name: item.name,
                            PortKey: item.key,
                            Type: Plegma.ePortType.String,
                            ioDirection: Plegma.ioPortDirection.Output,
                            State: '',
                        },
                        WebThingPortType.Data
                    ));
                    break;
                default:
                    break;
            }
        });

        return ports;
    }
    // ------------------------------------------------------------------------------------------------
    updateThing() {
        let widgetData = this.WidgetData,
            webNode = WebNode.GetInstanceKey(this.NodeKey),
            webThing = webNode.GetThing(this.ThingKey);

        webThing.Ports = {};

        // Add thing's ports.
        this.createThingPorts(widgetData).forEach((port) => {
            webThing.AddPort(port, false);
        });

        // Update thing.
        webNode.GetModule(this.WebModuleKey).UpdateThing(webThing);
    }
    // ------------------------------------------------------------------------------------------------
    initThing() {
        let widgetData = this.WidgetData,

            // Create plegma thing.
            plegmaThing = WidgetDescriptionToPlegmaThing(this.Description),

            // Create thing's ports and web thing.
            ports = this.createThingPorts(widgetData),
            webThing = new WebThing(this.WebModule, plegmaThing, ports);

        this.ThingKey = webThing.ThingKey;
    }
    // ------------------------------------------------------------------------------------------------
    /**
     * We don't have actual settings but only data form
     */
    public static GetDefaultWidgetDataStatic(): CommonListData { return {}; }
    // ------------------------------------------------------------------------------------------------
    public GetDefaultWidgetData() { return CommonListWidget.GetDefaultWidgetDataStatic(); }
    // ------------------------------------------------------------------------------------------------
    public static GetWidgetDesc(): IWidgetDescription {
        let data: CommonListData = CommonListWidget.GetDefaultWidgetDataStatic();

        // fill sample switch
        let portKey1 = getRandomSmallKey();
        data[portKey1] = {
            key: portKey1,
            name: 'Switch 1',
            description: 'Description',
            path: '/Switches/',
            order: 0,
            type: FolderItemType.Switch,
            extraData: {}
        };

        // fill sample button
        let portKey2 = getRandomSmallKey();
        data[portKey2] = {
            key: portKey2,
            name: 'Button 1',
            description: 'Description',
            path: '/Buttons/',
            order: 1,
            type: FolderItemType.Button,
            extraData: {}
        };

        // fill sample radio button
        let portKey3 = getRandomSmallKey();
        data[portKey3] = {
            key: portKey3,
            name: 'Period',
            description: 'Select Period',
            path: '/Buttons/',
            order: 2,
            type: FolderItemType.RadioButtons,
            extraData: {
                buttons: [
                    { label: '1 Day', value: "day", key: "1_" + getRandomSmallKey() },
                    { label: '1 Week', value: "week", key: "2_" + getRandomSmallKey() },
                    { label: '1 Month', value: "month", key: "3_" + getRandomSmallKey() }
                ] as IRadioButton[]
            } as IRadioButtons
        };

        // fill sample color picker
        let portKey4 = getRandomSmallKey();
        data[portKey4] = {
            key: portKey4,
            name: 'Color Picker 1',
            description: 'Description',
            path: '/Color Pickers/',
            order: 3,
            type: FolderItemType.ColorPicker,
            extraData: {
                pickerType: ColorPaletteMode.Sketch,
                color: 'rgba(0,0,0,1)'
            } as ColorPickerData
        };

        return {
            Title: 'Common List',
            WidgetTitle: '',
            Key: 'CommonListWidget',
            Type: 'CommonListWidget',
            Data: data,
            MinH: 1,
            MinW: 1,
            ShowExtraMenu: false,
            IconURI: WidgetsThingsStructs.IconURIPath + "/png/commonlist.png"
        };
    }
    // ------------------------------------------------------------------------------------------------
    public MigrateDefaultWidgetData(data: CommonListData): CommonListData {
        _.forEach(data, (value) => {
            if (value.type != FolderItemType.RadioButtons)
                return;

            if (!isDefined(value.extraData)) {
                DebugEx.TraceCritical("CommonList. Found radio button entry with null `extraData`. Should not be here");
                return;
            }

            const radioBtnData = value.extraData as IRadioButtons;
            for (let i = 0; i < radioBtnData.buttons.length; i++) {
                if (!isDefined(radioBtnData.buttons[i].key))
                    radioBtnData.buttons[i].key = i + "_" + getRandomSmallKey();
            }
        })
        return data;
    }

    // ------------------------------------------------------------------------------------------------
    public GetLocalizationDictionary() {
        let res = {};

        if (!IsNullOrEmpty(this.Description.WidgetTitle))
            res[WidgetsThingsStructs.WidgetLocalizationKeys.Title] = this.Description.WidgetTitle;

        _.forEach(this.WidgetData, (item) => {
            // add name
            res[item.key] = item.name;

            // add description (if any)
            if (isDefined(item.description))
                res[getKeyForDescription(item.key)] = item.description;

            // if radio btn; add buttons' labels
            if (item.type == FolderItemType.RadioButtons && isDefined(item.extraData)) {
                const radioBtnData = item.extraData as IRadioButtons;
                for (let i = 0; i < radioBtnData.buttons.length; i++) {
                    res[getKeyForRadioBtnLabel(item.key, radioBtnData.buttons[i].key)] = radioBtnData.buttons[i].label;
                }
            }
        });

        return res;
    }
}
// ------------------------------------------------------------------------------------------------
function getKeyForRadioBtnLabel(itemKey: string, buttonKey: string) {
    return itemKey + "radio_" + buttonKey;
}
// ------------------------------------------------------------------------------------------------
function getKeyForDescription(itemKey: string) {
    return itemKey + "_description";
}
// =======================================================================================================